import { DragEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';

import { Label, LABEL_TYPE } from '@/components/generic';
import { queryClient } from '@/configuration/react-query';
import {
  AUDIO,
  COVER_PHOTO,
  IMAGE,
  PLAYLIST,
  TEXT,
  TEXT_OVERLAY,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { getSlotTypeTranslation } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useMobileScreen } from '@/hooks';
import {
  DragIndicator,
  MusicIcon,
  PhotoIcon,
  SpeechIcon,
  TrashBlackIcon,
  VideoIcon
} from '@/resources';
import { Playlists } from '@/resources/icons/sidebar';
import musicThumbnail from '@/resources/img/music-thumbnail.png';
import playlistThumbnail from '@/resources/img/playlist-thumbnail.svg';
import textThumbnail from '@/resources/img/text-thumbnail.svg';
import { useUpdateAssetSlotMutation } from '@/services';
import { AssetSlotUpdate } from '@/types';
import { CeremonyTemplateSlotProps } from '@/types/CeremonyTemplateSlotTypes';

export const CeremonyTemplateSlot = ({
  type,
  slot,
  handleUpdateAssetOrder,
  deleteCeremonyTemplateSlot,
  refetchEvent,
  onClick,
  isReorderingEnabled,
  setIsReorderingEnabled,
  dataTestId
}: CeremonyTemplateSlotProps) => {
  const assetSlotDiv = useRef<HTMLDivElement | null>(null);
  const [src, setSrc] = useState<string | undefined>();
  const [onDrag, setOnDrag] = useState<null | string>(null);
  const [showTrashIcon, setShowTrashIcon] = useState(false);
  const [t] = useTranslation('common');
  const assetType = useRef<string | null>(null);
  const isLeftSideOfTargetDropDiv = useRef<boolean>(false);
  const dropAssetSlotDiv = useRef<HTMLDivElement | null>(null);
  const draggingAssetType = useRef<string>('');
  const [clickCount, setClickCount] = useState(0);

  const isMobile = useMobileScreen();

  const styles = {
    backgroundColor: type.backgroundColor,
    borderColor: type.borderColor,
    textColor: type.textColor
  };

  const { mutate: updateAssetSlot } = useUpdateAssetSlotMutation(queryClient, {
    onSuccess: () => {
      refetchEvent();
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const isAssetTypeMatching = (slotAssetType: string, assetType: string) => {
    return (
      slotAssetType === assetType ||
      (slotAssetType === COVER_PHOTO && assetType === IMAGE) ||
      (slotAssetType === IMAGE && assetType === COVER_PHOTO) ||
      (assetType === TEXT_OVERLAY && (slotAssetType === COVER_PHOTO || slotAssetType === IMAGE))
    );
  };

  const isDraggingAssetValid = (draggingType: string, dropZoneType: string) => {
    return (
      draggingType === dropZoneType ||
      (draggingType === COVER_PHOTO && dropZoneType === IMAGE) ||
      (draggingType === IMAGE && dropZoneType === COVER_PHOTO) ||
      (draggingType === TEXT_OVERLAY && (dropZoneType === COVER_PHOTO || dropZoneType === IMAGE)) ||
      draggingType === null
    );
  };

  const [, drag] = useDrag({
    type: 'type',
    item: { id: slot.id, index: slot.order },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: !isMobile || isReorderingEnabled
  });

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: 'type',
    collect: (monitor: { isOver: () => boolean; canDrop: () => boolean }) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    }),
    hover: (item: { index: number; id: string }, monitor) => {
      if (!assetSlotDiv.current) {
        return;
      }
      const hoverBoundingRect = assetSlotDiv.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const cursorPositionInTarget = clientOffset.x - hoverBoundingRect.left;
      const isActive = canDrop && isOver;

      const dragToLeft = cursorPositionInTarget < hoverBoundingRect.width / 2;
      isLeftSideOfTargetDropDiv.current = dragToLeft;

      if (dropAssetSlotDiv.current && isActive) {
        dropAssetSlotDiv.current.style.borderLeftColor = dragToLeft
          ? 'var(--primary-blue)'
          : 'transparent';
        dropAssetSlotDiv.current.style.borderRightColor = dragToLeft
          ? 'transparent'
          : 'var(--primary-blue)';
      } else if (dropAssetSlotDiv.current && !isActive) {
        dropAssetSlotDiv.current.style.borderLeftColor = 'transparent';
        dropAssetSlotDiv.current.style.borderRightColor = 'transparent';
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    drop: (item: { index: number; id: string }, monitor) => {
      if (!assetSlotDiv.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = slot.order;

      if (dragIndex === hoverIndex) {
        return;
      }

      handleUpdateAssetOrder(dragIndex, hoverIndex);

      item.index = hoverIndex;

      if (dropAssetSlotDiv.current) {
        dropAssetSlotDiv.current.style.borderLeftColor = 'transparent';
        dropAssetSlotDiv.current.style.borderRightColor = 'transparent';
      }
      setIsReorderingEnabled(false);
    }
  });
  const isActive = canDrop && isOver;

  const handleStart = (e: DragEvent) => {
    const mediaType = (e.target as HTMLDivElement).getAttribute('data-type');
    const src = (e.target as HTMLDivElement).getAttribute('data-src');

    assetType.current = mediaType;
    const dataTransfer = mediaType
      ? { id: (e.target as HTMLDivElement).id, src: src, mediaType }
      : { id: '', src: null, mediaType };

    e.dataTransfer.setData('application/json', JSON.stringify(dataTransfer));
    setOnDrag(null);
    draggingAssetType.current = mediaType as string;
  };

  const handleDrop = (e: DragEvent) => {
    setOnDrag(null);
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (data && data.startsWith('{') && data.endsWith('}')) {
      const dataTransfer = JSON.parse(data);

      if (isAssetTypeMatching(slot.slotType?.assetType as string, dataTransfer.mediaType)) {
        setSrc(dataTransfer.src);
        slot.assetId = Number(dataTransfer?.id);

        updateAssetSlot(slot as AssetSlotUpdate);
      }
    }
  };

  const handleDragOver = (e: DragEvent) => {
    if (assetSlotDiv.current) {
      const dropZoneSlotType = slot.slotType?.assetType;

      if (isDraggingAssetValid(draggingAssetType.current, dropZoneSlotType as string)) {
        e.dataTransfer.dropEffect = 'move';
        e.preventDefault();
      } else {
        e.dataTransfer.dropEffect = 'none';
      }
    }

    if (isAssetTypeMatching(slot.slotType?.assetType as string, assetType.current ?? '')) {
      e.preventDefault();
      setOnDrag(null);
    } else {
      setOnDrag('cursor-not-allowed');
    }
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    slot.assetId = undefined;
    slot.playlistId = undefined;
    setSrc(undefined);
    updateAssetSlot(slot as AssetSlotUpdate);
  };

  const handleDragLeave = () => {
    setOnDrag(null);
  };

  useEffect(() => {
    setAssetSrc();

    if (isActive) {
      return;
    }
    document.addEventListener('dragstart', handleStart as unknown as EventListener);
    if (assetSlotDiv.current) {
      assetSlotDiv.current.addEventListener('drop', handleDrop as unknown as EventListener);
      assetSlotDiv.current.addEventListener('dragover', handleDragOver as unknown as EventListener);
      assetSlotDiv.current.addEventListener('dragleave', handleDragLeave);
    }

    return () => {
      if (assetSlotDiv.current) {
        assetSlotDiv.current.removeEventListener('drop', handleDrop as unknown as EventListener);
        assetSlotDiv.current.removeEventListener(
          'dragover',
          handleDragOver as unknown as EventListener
        );
        assetSlotDiv.current.removeEventListener('dragleave', handleDragLeave);
      }
    };
  }, [slot]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropAssetSlotDiv.current && !dropAssetSlotDiv.current.contains(event.target as Node)) {
        setClickCount(0);
        setShowTrashIcon(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener);
    };
  }, []);

  const setAssetSrc = () => {
    if (slot?.asset) {
      const thumbnailUrl = slot.asset.thumbnailUrl;

      if (thumbnailUrl) {
        setSrc(thumbnailUrl);
      } else {
        const defaultThumbnail = slot.asset.assetType === AUDIO ? musicThumbnail : textThumbnail;
        setSrc(defaultThumbnail.toString());
      }
    } else if (slot?.playlistId) {
      const defaultThumbnail = playlistThumbnail;
      setSrc(defaultThumbnail.toString());
    } else {
      setSrc(undefined);
    }
  };

  useEffect(() => {
    if (isActive) {
      return;
    }
    setAssetSrc();
  }, [slot.asset, slot.playlist]);

  const showSlotIcon = () => {
    switch (type.assetType.toString()) {
      case IMAGE:
      case COVER_PHOTO:
        return <PhotoIcon fill={type.iconColor} className='size-5' />;
      case VIDEO:
      case TRIBUTE_VIDEO:
        return <VideoIcon fill={type.iconColor} className='size-5' />;
      case AUDIO:
        return <MusicIcon fill={type.iconColor} className='size-5' />;
      case TEXT:
      case TEXT_OVERLAY:
        return <SpeechIcon fill={type.iconColor} className='size-5' />;
      case PLAYLIST:
        return <Playlists style={{ color: type.iconColor }} className='size-5' />;
    }
  };

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsReorderingEnabled(true);
    const target = e.target as HTMLDivElement;
    if (target && target.draggable) {
      target.dispatchEvent(new DragEvent('dragstart', { bubbles: true }));
    }
  };

  const handleTouchEnd = () => {
    setIsReorderingEnabled(false);
  };
  drag(drop(dropAssetSlotDiv));

  const handleClick = () => {
    const clickCountLocal = clickCount + 1;
    if (clickCountLocal % 2 === 1) {
      setShowTrashIcon(true);
    } else {
      onClick();
    }
    setClickCount(clickCountLocal);
  };

  return (
    <div
      ref={dropAssetSlotDiv}
      className={`w-1/2 border border-transparent ${!isActive && 'border-0'} relative select-none p-2 md:w-1/4`}
      datatype={slot.order.toString()}
      data-testid={dataTestId}
      onClick={isMobile ? handleClick : onClick}>
      {showTrashIcon && (
        <div
          className='absolute bottom-0.5 left-0.5 z-50 flex h-10 w-9 items-center justify-center rounded-bl-2xl rounded-tr-xl bg-white/[0.6] pb-2 pl-1 md:hidden'
          onTouchStart={(e) => handleTouchStart(e)}
          onTouchEnd={handleTouchEnd}>
          <DragIndicator className='h-7 w-6 text-primary-blue' />
        </div>
      )}
      <div
        id={'slot-' + slot.id}
        ref={assetSlotDiv}
        className={` ${
          src && 'select-none !border-secondary-blue-gray'
        } relative flex aspect-square cursor-grab flex-col items-center justify-center rounded-lg border active:cursor-grabbing ${isActive && 'filter-pop-up-shadow'} ${onDrag}`}
        style={styles}
        onMouseEnter={(e) => {
          e.stopPropagation();
          !isMobile && setShowTrashIcon(true);
        }}
        onMouseLeave={() => setShowTrashIcon(false)}>
        {src ? (
          <div className='relative size-full'>
            {type.assetType.toString() === 'Audio' ||
            type.assetType.toString() === 'Text' ||
            type.assetType.toString() === 'Playlist' ? (
              <div className='relative size-full'>
                <img
                  className='disable-select aspect-square size-full rounded-lg object-cover'
                  alt=''
                  src={src}
                />
              </div>
            ) : (
              <img
                className='disable-select aspect-square size-full rounded-lg object-cover'
                alt=''
                src={src}
              />
            )}
            {showTrashIcon && (
              <div className='absolute right-2 top-2 z-10 flex size-6 cursor-pointer items-center justify-center rounded-md bg-blue-powder-light hover:bg-blue-powder'>
                <TrashBlackIcon
                  className='absolute right-1 top-1 size-4 cursor-pointer rounded-md'
                  onClick={handleDelete}
                />
              </div>
            )}
            {
              <div className='absolute bottom-2 left-1/2 w-[95%] -translate-x-1/2'>
                <Label
                  type={LABEL_TYPE.ASSET_SLOT_NAME_LABEL}
                  text={
                    slot.name
                      ? slot.name
                      : slot?.asset
                        ? slot.asset.assetName
                        : slot?.playlist?.name
                  }
                />
              </div>
            }
          </div>
        ) : (
          <div className='flex flex-col items-center'>
            <img alt='' src={type.iconUrl} />
            {showSlotIcon()}
            <Label text={getSlotTypeTranslation(type.id)} type={LABEL_TYPE.PARAGRAPH2} />
            {showTrashIcon && (
              <div className='absolute right-2 top-2 z-10 flex size-6 cursor-pointer items-center justify-center rounded-md bg-blue-powder-light hover:bg-blue-powder'>
                <TrashBlackIcon
                  className='absolute right-1 top-1 size-4 cursor-pointer rounded-md'
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCeremonyTemplateSlot?.(slot);
                  }}
                />
              </div>
            )}
            {!(slot.asset || slot.playlist) && slot.name && (
              <div className='absolute bottom-2 left-1/2 w-[95%] -translate-x-1/2'>
                <Label type={LABEL_TYPE.ASSET_SLOT_NAME_LABEL} text={slot.name} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
